import React from "react";
import {
	GraduationCap,
	LucideIcon,
	User,
	Users,
	UsersRound,
} from "lucide-react";
import { AudienceKey, AUDIENCES } from "../../hooks/useInviteePicker";
import "./eventManager.css";

interface EventAudienceChipsProps {
	audiences: Set<AudienceKey>;
	onToggle: (key: AudienceKey) => void;
	className?: string;
}

const AUDIENCE_ICONS: Record<AudienceKey, LucideIcon> = {
	groups: Users,
	students: GraduationCap,
	staff: User,
	parents: UsersRound,
};

/** One size for every chip icon, so nothing sits a pixel off. */
const ICON = 18;

/**
 * The "who is going" pills, shared by the mode chooser and the wizard's step 1.
 *
 * One component because the two screens had their own copies and had already
 * drifted: the chooser rendered icons, step 1 rendered bare labels, so the same
 * filter looked like two different controls depending on where you met it.
 * `data-audience` is what the stylesheet keys the per-category icon colour off,
 * and it was missing from the second copy entirely.
 *
 * Pressed toggles rather than checkboxes: these filter a list, they do not
 * select a value to be submitted.
 */
export const EventAudienceChips: React.FC<EventAudienceChipsProps> = ({
	audiences,
	onToggle,
	className,
}) => (
	<div className={className ? `em-start__chips ${className}` : "em-start__chips"}>
		{AUDIENCES.map((a) => {
			const Icon = AUDIENCE_ICONS[a.key];
			const on = audiences.has(a.key);
			return (
				<button
					key={a.key}
					type="button"
					aria-pressed={on}
					className={`em-start__chip${on ? " is-on" : ""}`}
					data-audience={a.key}
					onClick={() => onToggle(a.key)}
				>
					<Icon size={ICON} aria-hidden="true" />
					{a.label}
				</button>
			);
		})}
	</div>
);
